import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';

import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import {
  asApiError,
  safetyStockService,
  type AbcXyzKey,
  type SafetyStockEngineConfig,
  type SafetyStockSimulateSampleResponse,
} from '@/services/safetyStockService';
import {
  ServiceLevelMatrixEditor,
  type SegmentWeight,
} from '../components/ServiceLevelMatrixEditor';
import {
  SafetyStockSampleSimulateModal,
  type SampleSimulateModalMode,
} from '../components/SafetyStockSampleSimulateModal';
import { useSafetyStockApply } from '../components/SafetyStockModuleLayout';
import {
  useLatestAbcXyzBreakdown,
  useSafetyStockConfig,
  useSafetyStockHealth,
  useSafetyStockScope,
  useSaveSafetyStockConfig,
} from '../hooks/useSafetyStockConfig';
import {
  cloneConfig,
  configsEqual,
  fractionToPercentInput,
  percentInputToFraction,
  validateSafetyStockConfig,
} from '../utils/configValidation';

const SAMPLE_SIZE = 200;

const fieldClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary';

function Section({
  title,
  help,
  children,
}: {
  title: string;
  help: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <p className="mt-1 text-sm text-gray-500">{help}</p>
      </div>
      {children}
    </section>
  );
}

export function SafetyStockParametersPage() {
  const { t } = useTranslation();
  const warehouseId = useWarehouseId();
  const { isActionEnabled } = useFeatureVisibility();
  const applyEnabled = isActionEnabled('safety_stock.apply', false);

  const configQuery = useSafetyStockConfig();
  const healthQuery = useSafetyStockHealth();
  const saveMutation = useSaveSafetyStockConfig();
  const breakdownQuery = useLatestAbcXyzBreakdown(warehouseId);

  const [draft, setDraft] = useState<SafetyStockEngineConfig | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);
  const [sampleModalOpen, setSampleModalOpen] = useState(false);
  const [sampleModalMode, setSampleModalMode] =
    useState<SampleSimulateModalMode>('confirm');
  const [sampleResult, setSampleResult] =
    useState<SafetyStockSimulateSampleResponse | null>(null);
  const [sampleError, setSampleError] = useState<string | null>(null);

  useEffect(() => {
    if (configQuery.data?.config) {
      setDraft(cloneConfig(configQuery.data.config));
    }
  }, [configQuery.data?.config]);

  const scopeQuery = useSafetyStockScope(
    draft?.active_products_only ?? true,
    warehouseId,
    Boolean(draft),
  );

  const dirty = useMemo(() => {
    if (!draft || !configQuery.data?.config) return false;
    return !configsEqual(draft, configQuery.data.config);
  }, [draft, configQuery.data?.config]);

  const fieldErrors = useMemo(
    () => (draft ? validateSafetyStockConfig(draft) : {}),
    [draft],
  );
  const hasErrors = Object.keys(fieldErrors).length > 0;

  const weights = useMemo(() => {
    const map: Partial<Record<string, SegmentWeight>> = {};
    for (const row of breakdownQuery.data?.rows || []) {
      const key = String(row.dimension_value || '').toUpperCase();
      if (!key) continue;
      map[key] = {
        items: Number(row.items || 0),
        value: Number(row.safety_stock_value || 0),
      };
    }
    return map;
  }, [breakdownQuery.data]);

  const engineDown =
    healthQuery.isError ||
    (healthQuery.data && healthQuery.data.status && healthQuery.data.status !== 'ok');

  const applyFlow = useSafetyStockApply();

  const patch = <K extends keyof SafetyStockEngineConfig>(
    key: K,
    value: SafetyStockEngineConfig[K],
  ) => {
    setDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSaveOk(false);
    setBannerError(null);
  };

  const patchNested = (
    group: 'outliers' | 'rounding' | 'fallback',
    key: string,
    value: unknown,
  ) => {
    setDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [group]: { ...(prev[group] || {}), [key]: value },
      };
    });
    setSaveOk(false);
    setBannerError(null);
  };

  const handleDiscard = () => {
    if (configQuery.data?.config) {
      setDraft(cloneConfig(configQuery.data.config));
      setBannerError(null);
      setSaveOk(false);
    }
  };

  const handleSave = async (): Promise<boolean> => {
    if (!draft || hasErrors) return false;
    setBannerError(null);
    setSaveOk(false);
    try {
      const payload = {
        ...draft,
        configured_safety_days: Number(draft.configured_safety_days),
        maximum_safety_days: Number(draft.maximum_safety_days),
        adi_threshold: Number(draft.adi_threshold),
        cv_squared_threshold: Number(draft.cv_squared_threshold),
        default_service_level: Number(draft.default_service_level),
      };
      const result = await saveMutation.mutateAsync(payload);
      setDraft(cloneConfig(result.config));
      setSaveOk(true);
      return true;
    } catch (err) {
      const apiErr = asApiError(err);
      setBannerError(apiErr.detail || apiErr.message);
      return false;
    }
  };

  const handleSaveAndApply = async () => {
    const ok = dirty ? await handleSave() : !hasErrors;
    if (!ok) return;
    if (scopeQuery.data?.exceeds_max) {
      setBannerError(t('safetyStock.parameters.scopeExceedsMax'));
      return;
    }
    applyFlow.requestApply(scopeQuery.data?.total_items ?? null);
  };

  const openSampleModal = () => {
    setSampleResult(null);
    setSampleError(null);
    setSampleModalMode('confirm');
    setSampleModalOpen(true);
  };

  const closeSampleModal = () => {
    if (sampleModalMode === 'progress') return;
    setSampleModalOpen(false);
    setSampleModalMode('confirm');
    setSampleError(null);
  };

  const runSampleSimulation = async () => {
    setSampleModalMode('progress');
    setSampleResult(null);
    setSampleError(null);
    setBannerError(null);
    try {
      if (dirty) {
        const ok = await handleSave();
        if (!ok) {
          setSampleModalMode('error');
          setSampleError(t('safetyStock.parameters.sampleModal.saveBeforeFailed'));
          return;
        }
      }
      const result = await safetyStockService.simulateSample(
        SAMPLE_SIZE,
        warehouseId as number,
      );
      setSampleResult(result);
      setSampleModalMode('result');
    } catch (err) {
      const apiErr = asApiError(err);
      setSampleError(apiErr.message);
      setSampleModalMode('error');
    }
  };

  if (configQuery.isLoading || !draft) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('safetyStock.parameters.loading')}
      </div>
    );
  }

  if (configQuery.isError) {
    return (
      <div className="m-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        {t('safetyStock.parameters.loadError')}
      </div>
    );
  }

  const errLabel = (key: string) =>
    fieldErrors[key] ? t(`safetyStock.parameters.errors.${fieldErrors[key]}`) : null;

  return (
    <div className="flex w-full min-w-0 max-w-full flex-col gap-6 p-4 md:p-6">
      {configQuery.data?.created ? (
        <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          {t('safetyStock.parameters.defaultsNotice')}
        </div>
      ) : null}

      {engineDown ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {t('safetyStock.parameters.engineDown')}
        </div>
      ) : null}

      {bannerError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {bannerError}
        </div>
      ) : null}

      {saveOk && !dirty ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {t('safetyStock.parameters.saved')}
        </div>
      ) : null}

      <Section
        title={t('safetyStock.parameters.sections.serviceLevel.title')}
        help={t('safetyStock.parameters.sections.serviceLevel.help')}
      >
        <div className="mb-4 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.serviceMeasure')}
            </span>
            <select
              className={fieldClass}
              value={draft.service_measure}
              onChange={(e) =>
                patch('service_measure', e.target.value as SafetyStockEngineConfig['service_measure'])
              }
            >
              <option value="CYCLE_SERVICE_LEVEL">
                {t('safetyStock.parameters.enums.serviceMeasure.CYCLE_SERVICE_LEVEL')}
              </option>
              <option value="FILL_RATE">
                {t('safetyStock.parameters.enums.serviceMeasure.FILL_RATE')}
              </option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.defaultServiceLevel')}
            </span>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                className={fieldClass}
                value={fractionToPercentInput(draft.default_service_level)}
                onChange={(e) => {
                  const fraction = percentInputToFraction(e.target.value);
                  if (fraction != null) patch('default_service_level', fraction);
                }}
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-400">
                %
              </span>
            </div>
            {errLabel('default_service_level') ? (
              <span className="mt-1 block text-xs text-red-600">
                {errLabel('default_service_level')}
              </span>
            ) : null}
          </label>
        </div>
        <ServiceLevelMatrixEditor
          values={draft.service_levels}
          weights={weights}
          errors={fieldErrors}
          onChange={(key: AbcXyzKey, fraction) => {
            if (fraction == null) return;
            setDraft((prev) =>
              prev
                ? {
                    ...prev,
                    service_levels: { ...prev.service_levels, [key]: fraction },
                  }
                : prev,
            );
            setSaveOk(false);
          }}
        />
      </Section>

      <Section
        title={t('safetyStock.parameters.sections.coverage.title')}
        help={t('safetyStock.parameters.sections.coverage.help')}
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.timeUnit')}
            </span>
            <select
              className={fieldClass}
              value={draft.time_unit}
              onChange={(e) =>
                patch('time_unit', e.target.value as SafetyStockEngineConfig['time_unit'])
              }
            >
              {(['DAY', 'WEEK', 'MONTH'] as const).map((u) => (
                <option key={u} value={u}>
                  {t(`safetyStock.parameters.enums.timeUnit.${u}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.configuredSafetyDays')}
            </span>
            <input
              type="number"
              min={0}
              step="0.1"
              className={fieldClass}
              value={draft.configured_safety_days}
              onChange={(e) => patch('configured_safety_days', e.target.value)}
            />
            {errLabel('configured_safety_days') ? (
              <span className="mt-1 block text-xs text-red-600">
                {errLabel('configured_safety_days')}
              </span>
            ) : null}
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.maximumSafetyDays')}
            </span>
            <input
              type="number"
              min={0}
              step="0.1"
              className={fieldClass}
              value={draft.maximum_safety_days}
              onChange={(e) => patch('maximum_safety_days', e.target.value)}
            />
            {errLabel('maximum_safety_days') ? (
              <span className="mt-1 block text-xs text-red-600">
                {errLabel('maximum_safety_days')}
              </span>
            ) : null}
          </label>
        </div>
      </Section>

      <Section
        title={t('safetyStock.parameters.sections.dataMin.title')}
        help={t('safetyStock.parameters.sections.dataMin.help')}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.minLeadTimeObs')}
            </span>
            <input
              type="number"
              min={1}
              className={fieldClass}
              value={draft.minimum_lead_time_observations}
              onChange={(e) =>
                patch('minimum_lead_time_observations', Number(e.target.value) || 0)
              }
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.minPositiveObs')}
            </span>
            <input
              type="number"
              min={1}
              className={fieldClass}
              value={draft.minimum_positive_observations}
              onChange={(e) =>
                patch('minimum_positive_observations', Number(e.target.value) || 0)
              }
            />
          </label>
        </div>
      </Section>

      <Section
        title={t('safetyStock.parameters.sections.outliers.title')}
        help={t('safetyStock.parameters.sections.outliers.help')}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.outlierMethod')}
            </span>
            <select
              className={fieldClass}
              value={draft.outliers?.outlier_method || 'IQR'}
              onChange={(e) => patchNested('outliers', 'outlier_method', e.target.value)}
            >
              <option value="IQR">IQR</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.outlierAction')}
            </span>
            <select
              className={fieldClass}
              value={draft.outliers?.outlier_action || 'REMOVE'}
              onChange={(e) => patchNested('outliers', 'outlier_action', e.target.value)}
            >
              {['REMOVE', 'CAP', 'KEEP', 'MANUAL_REVIEW'].map((a) => (
                <option key={a} value={a}>
                  {t(`safetyStock.parameters.enums.outlierAction.${a}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.iqrMultiplier')}
            </span>
            <input
              type="number"
              step="0.1"
              className={fieldClass}
              value={draft.outliers?.iqr_multiplier ?? 1.5}
              onChange={(e) =>
                patchNested('outliers', 'iqr_multiplier', Number(e.target.value))
              }
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.iqrLower')}
            </span>
            <input
              type="number"
              step="0.01"
              className={fieldClass}
              value={draft.outliers?.iqr_lower_quantile ?? 0.25}
              onChange={(e) =>
                patchNested('outliers', 'iqr_lower_quantile', Number(e.target.value))
              }
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.iqrUpper')}
            </span>
            <input
              type="number"
              step="0.01"
              className={fieldClass}
              value={draft.outliers?.iqr_upper_quantile ?? 0.75}
              onChange={(e) =>
                patchNested('outliers', 'iqr_upper_quantile', Number(e.target.value))
              }
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(draft.outliers?.apply_outlier_filter_to_demand)}
              onChange={(e) =>
                patchNested('outliers', 'apply_outlier_filter_to_demand', e.target.checked)
              }
            />
            {t('safetyStock.parameters.fields.applyOutlierDemand')}
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={Boolean(draft.outliers?.apply_outlier_filter_to_lead_time)}
              onChange={(e) =>
                patchNested(
                  'outliers',
                  'apply_outlier_filter_to_lead_time',
                  e.target.checked,
                )
              }
            />
            {t('safetyStock.parameters.fields.applyOutlierLeadTime')}
          </label>
        </div>
      </Section>

      <Section
        title={t('safetyStock.parameters.sections.rounding.title')}
        help={t('safetyStock.parameters.sections.rounding.help')}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.roundingMethod')}
            </span>
            <select
              className={fieldClass}
              value={draft.rounding?.rounding_method || 'CEIL'}
              onChange={(e) => patchNested('rounding', 'rounding_method', e.target.value)}
            >
              {['CEIL', 'FLOOR', 'NEAREST', 'ORDER_MULTIPLE', 'PACKAGE_MULTIPLE'].map((m) => (
                <option key={m} value={m}>
                  {t(`safetyStock.parameters.enums.roundingMethod.${m}`)}
                </option>
              ))}
            </select>
          </label>
          {(
            [
              ['rounding_multiple', 'roundingMultiple'],
              ['minimum_safety_stock', 'minimumSafetyStock'],
              ['maximum_safety_stock', 'maximumSafetyStock'],
              ['minimum_coverage_days', 'minimumCoverageDays'],
              ['maximum_coverage_days', 'maximumCoverageDays'],
            ] as const
          ).map(([field, labelKey]) => (
            <label key={field} className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                {t(`safetyStock.parameters.fields.${labelKey}`)}
              </span>
              <input
                type="number"
                className={fieldClass}
                value={draft.rounding?.[field] ?? ''}
                onChange={(e) =>
                  patchNested(
                    'rounding',
                    field,
                    e.target.value === '' ? null : Number(e.target.value),
                  )
                }
              />
            </label>
          ))}
        </div>
      </Section>

      <Section
        title={t('safetyStock.parameters.sections.fallback.title')}
        help={t('safetyStock.parameters.sections.fallback.help')}
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.fallbackMethod')}
            </span>
            <select
              className={fieldClass}
              value={draft.fallback?.fallback_method || 'DAYS_OF_COVERAGE'}
              onChange={(e) => patchNested('fallback', 'fallback_method', e.target.value)}
            >
              {[
                'DAYS_OF_COVERAGE',
                'FAMILY_DISTRIBUTION',
                'CATEGORY_DISTRIBUTION',
                'SIMILAR_PRODUCTS',
                'MINIMUM_CONFIGURED_STOCK',
                'NO_CALCULATION',
              ].map((m) => (
                <option key={m} value={m}>
                  {t(`safetyStock.parameters.enums.fallbackMethod.${m}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.fallbackSafetyDays')}
            </span>
            <input
              type="number"
              className={fieldClass}
              value={draft.fallback?.fallback_safety_days ?? 7}
              onChange={(e) =>
                patchNested('fallback', 'fallback_safety_days', Number(e.target.value))
              }
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.fallbackMinStock')}
            </span>
            <input
              type="number"
              className={fieldClass}
              value={draft.fallback?.fallback_minimum_safety_stock ?? 0}
              onChange={(e) =>
                patchNested(
                  'fallback',
                  'fallback_minimum_safety_stock',
                  Number(e.target.value),
                )
              }
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.fallbackGrouping')}
            </span>
            <input
              type="text"
              className={fieldClass}
              value={draft.fallback?.fallback_grouping_level || 'family'}
              onChange={(e) =>
                patchNested('fallback', 'fallback_grouping_level', e.target.value)
              }
            />
          </label>
        </div>
      </Section>

      <Section
        title={t('safetyStock.parameters.sections.simulation.title')}
        help={t('safetyStock.parameters.sections.simulation.help')}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.numberOfSimulations')}
            </span>
            <input
              type="number"
              min={1}
              className={fieldClass}
              value={draft.number_of_simulations}
              onChange={(e) =>
                patch('number_of_simulations', Number(e.target.value) || 0)
              }
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              {t('safetyStock.parameters.fields.randomSeed')}
            </span>
            <input
              type="number"
              className={fieldClass}
              value={draft.random_seed}
              onChange={(e) => patch('random_seed', Number(e.target.value) || 0)}
            />
          </label>
        </div>
      </Section>

      <Section
        title={t('safetyStock.parameters.sections.scope.title')}
        help={t('safetyStock.parameters.sections.scope.help')}
      >
        <label className="mb-3 inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.active_products_only}
            onChange={(e) => patch('active_products_only', e.target.checked)}
          />
          {t('safetyStock.parameters.fields.activeProductsOnly')}
        </label>
        <p className="text-sm text-gray-700">
          {scopeQuery.isLoading
            ? t('safetyStock.parameters.scopeLoading')
            : t('safetyStock.parameters.scopeSummary', {
                count: scopeQuery.data?.total_items ?? 0,
                excluded: scopeQuery.data?.excluded_no_warehouse ?? 0,
              })}
        </p>
        {scopeQuery.data?.exceeds_max ? (
          <p className="mt-2 text-sm text-red-700">
            {t('safetyStock.parameters.scopeExceedsMax')}
          </p>
        ) : null}
      </Section>

      <div className="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur md:-mx-6 md:px-6">
        <div className="min-w-0">
          <button
            type="button"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
            disabled={
              (sampleModalOpen && sampleModalMode === 'progress') || Boolean(engineDown)
            }
            onClick={openSampleModal}
          >
            {t('safetyStock.parameters.simulateSample')}
          </button>
          <p className="mt-1 max-w-sm text-xs text-gray-500">
            {t('safetyStock.parameters.simulateSampleHint', {
              sample: SAMPLE_SIZE,
              population: scopeQuery.data?.total_items ?? '—',
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
            disabled={!dirty || saveMutation.isPending}
            onClick={handleDiscard}
          >
            {t('safetyStock.parameters.discard')}
          </button>
          <button
            type="button"
            className="rounded-md border border-stocker-primary px-4 py-2 text-sm font-medium text-stocker-primary hover:bg-stocker-primary/5 disabled:opacity-50"
            disabled={!dirty || hasErrors || saveMutation.isPending}
            onClick={() => void handleSave()}
          >
            {saveMutation.isPending
              ? t('safetyStock.parameters.saving')
              : t('safetyStock.parameters.save')}
          </button>
          <button
            type="button"
            className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            disabled={
              hasErrors ||
              saveMutation.isPending ||
              applyFlow.isRunning ||
              !applyEnabled ||
              Boolean(engineDown) ||
              Boolean(scopeQuery.data?.exceeds_max)
            }
            title={
              !applyEnabled
                ? t('safetyStock.apply.errors.applyDisabled')
                : engineDown
                  ? t('safetyStock.parameters.engineDown')
                  : undefined
            }
            onClick={() => void handleSaveAndApply()}
          >
            {t('safetyStock.parameters.saveAndApply')}
          </button>
        </div>
      </div>

      <SafetyStockSampleSimulateModal
        open={sampleModalOpen}
        mode={sampleModalMode}
        sampleSize={SAMPLE_SIZE}
        populationSize={scopeQuery.data?.total_items}
        result={sampleResult}
        errorMessage={sampleError}
        onConfirm={() => void runSampleSimulation()}
        onClose={closeSampleModal}
        onRetry={() => void runSampleSimulation()}
      />
    </div>
  );
}
